import { useState } from 'react';
import { useAuthStore } from '../../lib/auth.js';
import { ApiError } from '../../lib/api.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { SendModalShell } from '../send/SendModalShell.js';
import { audToCents, centsToAudDisplay, centsToInput } from '../../lib/money.js';
import { bpToInput, percentToBp } from '../../lib/percent.js';
import {
  activeScholarship,
  buildScholarshipConfirm,
  isNegativeNet,
  scholarshipLabel,
  scholarshipVersionLabel,
} from '../../lib/pricing.js';
import { editorLabel } from '../../lib/editor-label.js';
import { formatDate } from '../../lib/format.js';
import {
  useCourseScholarshipHistory,
  useSetCourseScholarship,
  type PricingCourseRow,
  type SetCourseScholarshipBody,
} from '../../hooks/usePricing.js';

/** Local "are you sure?" dialog — mirrors the one in PricingDetailPanel (no shared component). */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <SendModalShell open={open} onClose={onCancel} blocking={loading}>
      <div className="p-6">
        <h2 className="font-serif text-xl tracking-tight text-ink">{title}</h2>
        <p className="mt-2 text-sm text-muted">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </SendModalShell>
  );
}

/**
 * Scholarship block for the pricing detail panel (5c): current value, the
 * SERVER-computed net fee, percent/amount/remove editing with the same
 * confirm-before-save ceremony as the fee editor above it, and the version
 * history. The net fee is always `netFeeCents` from the list row — the browser
 * never computes fee minus scholarship itself.
 */
export function ScholarshipSection({ course }: { course: PricingCourseRow }) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const history = useCourseScholarshipHistory(course.courseId);
  const setScholarship = useSetCourseScholarship();

  // Rows from a pre-scholarship fixture omit the 5b fields: undefined ≡ null.
  const scholarship = course.scholarship ?? null;
  const current = activeScholarship(scholarship);
  const netFeeCents = course.netFeeCents ?? null;

  const [kind, setKind] = useState<'percent' | 'amount'>(() =>
    current?.kind === 'amount' ? 'amount' : 'percent',
  );
  const [value, setValue] = useState(() =>
    current?.kind === 'percent'
      ? bpToInput(current.percentBp)
      : current?.kind === 'amount'
        ? centsToInput(current.amountCents)
        : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // The pending save (dialog open ⇔ intent !== null) — Save and Remove share the dialog.
  const [intent, setIntent] = useState<SetCourseScholarshipBody | null>(null);

  const parsedBp = kind === 'percent' ? percentToBp(value) : null;
  const parsedCents = kind === 'amount' ? audToCents(value) : null;
  const valid = kind === 'percent' ? parsedBp !== null : parsedCents !== null && parsedCents > 0;
  const busy = setScholarship.isPending;
  const showInputError = value.trim() !== '' && !valid;

  // Same rule as the fee history: current = newest version effective on/before today.
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD (local)
  const currentVersionId = history.data?.history.find((v) => v.validFrom <= today)?.id ?? null;

  function switchKind(next: 'percent' | 'amount') {
    setKind(next);
    setError(null);
    setSuccess(null);
  }

  function requestSave() {
    if (kind === 'percent') {
      if (parsedBp === null) return;
      setIntent({ kind: 'percent', percentBp: parsedBp });
    } else {
      if (parsedCents === null || parsedCents <= 0) return;
      setIntent({ kind: 'amount', amountCents: parsedCents });
    }
  }

  async function commit() {
    if (!intent) return;
    setError(null);
    setSuccess(null);
    try {
      await setScholarship.mutateAsync({ masterCourseId: course.courseId, body: intent });
      setSuccess(
        intent.kind === 'none'
          ? 'Scholarship removed.'
          : `Scholarship set to ${scholarshipLabel(intent)}.`,
      );
      setIntent(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <div className="mt-6 border-t border-line pt-6">
      <h3 className="font-serif text-base text-ink">Scholarship</h3>

      {/* Current scholarship — straight from the list row */}
      <div className="mt-2">
        <div className="text-[11px] uppercase tracking-wide text-muted">Current scholarship</div>
        <div className="text-lg font-medium text-navy">
          {current ? scholarshipLabel(current) : 'No scholarship'}
        </div>
        {current && scholarship && (
          <div className="text-xs text-muted">Effective {formatDate(scholarship.validFrom)}</div>
        )}
      </div>

      {/* Net fee — ALWAYS the server's netFeeCents, never computed in the browser */}
      {current && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-muted">
            Net fee (after scholarship)
          </div>
          {netFeeCents === null ? (
            <>
              <div className="text-lg font-medium text-navy">—</div>
              <p className="text-xs text-muted">No agency fee is set yet.</p>
            </>
          ) : isNegativeNet(netFeeCents) ? (
            <div className="mt-1 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger">
              Net fee: {centsToAudDisplay(netFeeCents)} — the scholarship now exceeds the agency
              fee, so the net fee is negative. Correct the scholarship or the fee.
            </div>
          ) : (
            <div className="text-lg font-medium text-navy">{centsToAudDisplay(netFeeCents)}</div>
          )}
        </div>
      )}

      {/* Set / change / remove */}
      <div className="mt-4">
        <div className="flex gap-2" role="group" aria-label="Scholarship type">
          <Button
            type="button"
            size="sm"
            variant={kind === 'percent' ? 'secondary' : 'ghost'}
            className={kind === 'percent' ? 'border-navy/50' : undefined}
            aria-pressed={kind === 'percent'}
            disabled={busy}
            onClick={() => switchKind('percent')}
          >
            Percent
          </Button>
          <Button
            type="button"
            size="sm"
            variant={kind === 'amount' ? 'secondary' : 'ghost'}
            className={kind === 'amount' ? 'border-navy/50' : undefined}
            aria-pressed={kind === 'amount'}
            disabled={busy}
            onClick={() => switchKind('amount')}
          >
            Amount
          </Button>
        </div>
        <label htmlFor="scholarship-input" className="mb-1 mt-3 block text-sm text-ink">
          {kind === 'percent' ? 'Scholarship percent (%)' : 'Scholarship amount (AUD)'}
        </label>
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              id="scholarship-input"
              inputMode="decimal"
              placeholder={kind === 'percent' ? '10' : '0.00'}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
                setSuccess(null);
              }}
              invalid={showInputError}
            />
            {showInputError && (
              <p className="mt-1 text-xs text-danger">
                {kind === 'percent'
                  ? 'Enter a percent from 0.01 to 100, max 2 decimals.'
                  : 'Enter an amount above zero — numbers only, max 2 decimals.'}
              </p>
            )}
          </div>
          <Button onClick={requestSave} disabled={!valid || busy}>
            Save scholarship
          </Button>
          {current && (
            <Button variant="secondary" disabled={busy} onClick={() => setIntent({ kind: 'none' })}>
              Remove scholarship
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 rounded-lg border border-success/30 bg-success-bg px-3 py-2 text-sm text-success">
          {success}
        </div>
      )}

      {/* Scholarship history */}
      <div className="mt-6">
        <h3 className="mb-2 font-serif text-base text-ink">Scholarship history</h3>
        {history.isLoading && <p className="text-sm text-muted">Loading history…</p>}
        {history.error && <p className="text-sm text-danger">Couldn't load the history.</p>}
        {!history.isLoading && !history.error && (history.data?.history.length ?? 0) === 0 && (
          <p className="text-sm text-muted">No scholarship has been set yet.</p>
        )}
        {!history.isLoading && !history.error && (history.data?.history.length ?? 0) > 0 && (
          <ul className="divide-y divide-line">
            {history.data!.history.map((v) => (
              <li key={v.id} className="py-2">
                <div className="text-sm text-ink">
                  {scholarshipVersionLabel(v)}
                  {v.id === currentVersionId && (
                    <span className="badge ml-2 bg-success-bg text-success">Current</span>
                  )}
                </div>
                <div className="text-xs text-muted">
                  Effective {formatDate(v.validFrom)} · {editorLabel(v, currentUserId)} ·{' '}
                  {formatDate(v.createdAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={intent !== null}
        title="Course scholarship"
        body={intent ? buildScholarshipConfirm(current, intent) : ''}
        confirmLabel={intent?.kind === 'none' ? 'Remove scholarship' : 'Set scholarship'}
        loading={busy}
        onConfirm={() => void commit()}
        onCancel={() => setIntent(null)}
      />
    </div>
  );
}
